

import { Button, Alert } from "@a2z/ui";

import { useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { getInviteAbsoluteUrl } from "@/lib/routing/portal-urls";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useReissueSellerInviteMutation } from "../services/useReissueSellerInviteMutation";
import { ShareLinkButton } from "@/shared/components/ShareLinkButton";

export function InviteReissueButton({
  sellerId,
  locale,
}: {
  sellerId: string;
  locale: string;
}) {
  const t = useTranslations();
  const [error, setError] = useState<string | null>(null);
  const [invite, setInvite] = useState<{ inviteToken: string; expiresAt: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInviteDetailsOpen, setIsInviteDetailsOpen] = useState(false);
  const { mutateAsync: reissueInvite } = useReissueSellerInviteMutation();

  const inviteLink =
    invite?.inviteToken && typeof window !== "undefined"
      ? getInviteAbsoluteUrl(
          locale,
          invite.inviteToken,
          window.location.protocol === "http:" ? "http" : "https",
        )
      : "";

  const handleClick = async () => {
    setError(null);
    setInvite(null);
    setIsSubmitting(true);
    try {
      const payload = await reissueInvite({ sellerId });
      setInvite({
        inviteToken: payload.inviteToken,
        expiresAt: payload.expiresAt,
      });
      setIsInviteDetailsOpen(false);
    } catch (error) {
      if (error instanceof ApiJsonClientError) {
        setError(error.message || t(keys.errors.generic));
      } else {
        setError(t(keys.errors.generic));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {error ? <Alert message={error} helperText={t(keys.errors.recovery)} /> : null}
      {invite ? (
        <div className="rounded-xl border border-[color:var(--border-light)] bg-[color:var(--background-muted)] px-4 py-3 text-sm text-[color:var(--text-secondary)]">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="font-semibold text-[color:var(--text-primary)]">
              {t(keys.admin.inviteToken)}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                onClick={() => setIsInviteDetailsOpen((current) => !current)}
                variant="outline" className="h-8 rounded-full px-3 text-xs font-semibold transition max-lg:h-10"
                aria-expanded={isInviteDetailsOpen}
                aria-controls="reissue-invite-details"
              >
                {isInviteDetailsOpen
                  ? t(keys.admin.inviteDetailsHide)
                  : t(keys.admin.inviteDetailsShow)}
              </Button>
              {inviteLink ? (
                <ShareLinkButton
                  path={inviteLink}
                  label={t(keys.common.copyInviteLink)}
                  copiedLabel={t(keys.common.copied)}
                  ariaLabel={t(keys.common.copyInviteLink)}
                  size="sm"
                  className="h-8"
                  toastMessage={t(keys.admin.inviteLinkCopiedToast)}
                />
              ) : null}
            </div>
          </div>
          {isInviteDetailsOpen ? (
            <div id="reissue-invite-details" className="mt-3 min-w-0 space-y-2">
              <div className="text-xs text-[color:var(--text-muted)]">
                <span className="font-semibold text-[color:var(--text-secondary)]">
                  {t(keys.admin.inviteToken)}:
                </span>{" "}
                <span className="break-all">{invite.inviteToken}</span>
              </div>
              {inviteLink ? (
                <div className="min-w-0 text-xs text-[color:var(--text-muted)]">
                  <span className="font-semibold text-[color:var(--text-secondary)]">
                    {t(keys.common.copyInviteLink)}:
                  </span>{" "}
                  <span className="break-all">{inviteLink}</span>
                </div>
              ) : null}
              <div className="text-xs text-[color:var(--text-muted)]">
                {t(keys.admin.inviteExpires)}: {invite.expiresAt}
              </div>
            </div>
          ) : (
            <div className="mt-2 text-xs text-[color:var(--text-muted)]">
              {t(keys.admin.inviteDetailsCollapsedHint)}
            </div>
          )}
        </div>
      ) : null}
      <Button
        type="button"
        onClick={handleClick}
        disabled={isSubmitting}
        variant="outline" className="h-11 rounded-full px-4 text-sm font-semibold transition disabled:opacity-60"
      >
        {t(keys.admin.reissueInvite)}
      </Button>
    </div>
  );
}
